/**
 * RECORDING A MEASUREMENT — the six-column grid.
 *
 * WHY IT LOOKS LIKE THE PAPER BOOK
 *
 * Description | Nos | Length | Breadth | Depth | Quantity. Those six columns
 * have been the Measurement Book since the PWD wrote its first code, and the
 * engineer using this screen has kept one by hand for twenty years. A cleverer
 * layout would be a worse one: he must be able to copy his site sheet into it
 * in the order he wrote it, without translating anything.
 *
 * THE THREE THINGS THE SCREEN DOES THAT PAPER CANNOT
 *
 *   1. The quantity is COMPUTED — nos x L x B x D, absent dimensions taken as
 *      1, so a running metre is nos x length and not nos x length x 0. Typed
 *      arithmetic at seven in the evening under a torch is where the book's
 *      errors have always come from.
 *
 *   2. A DEDUCTION IS WRITTEN AS POSITIVE DIMENSIONS. An opening 1.2 x 2.1 is
 *      entered as 1.2 and 2.1 on a line ticked "deduct", and the quantity comes
 *      out negative on its own. Nobody types a minus sign, because on paper
 *      nobody ever has, and a minus sign typed by mistake into the LENGTH
 *      column is an error the eye does not catch.
 *
 *   3. THE CEILING IS SHOWN WHILE YOU TYPE. Against every work order line:
 *      ordered and varied, already certified, what this page adds, what is
 *      left. The moment a line goes past its ceiling the panel turns red and
 *      says what to do about it — because finding out at certification, a week
 *      later, through an exception, wastes the engineer's afternoon and the
 *      RCM's.
 *
 * The server refuses an over-run at certification regardless
 * (fn_mb_assert_certifiable, migration 0119). This screen is the courtesy; the
 * trigger is the control.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, Info, Minus, Plus, Ruler, Trash2, TriangleAlert,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useQuery, useSites } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { qty, rupees } from '@/lib/format'
import {
  Alert, Card, Field, Input, Loading, Select, Textarea, useToast,
} from '@/components/ui'
import { serviceCan } from '../nav'

const n = (v: string) => Number(v) || 0

interface Row {
  key: string
  wo_line_id: string
  description: string
  is_deduction: boolean
  nos: string
  dim_l: string
  dim_b: string
  dim_d: string
  remarks: string
}

const blank = (): Row => ({
  key: Math.random().toString(36).slice(2),
  wo_line_id: '', description: '', is_deduction: false,
  nos: '1', dim_l: '', dim_b: '', dim_d: '', remarks: '',
})

/** The book's own arithmetic. An absent dimension is 1, never 0. */
function lineQty(r: Row): number {
  const v = n(r.nos)
    * (r.dim_l === '' ? 1 : n(r.dim_l))
    * (r.dim_b === '' ? 1 : n(r.dim_b))
    * (r.dim_d === '' ? 1 : n(r.dim_d))
  const rounded = Math.round(v * 1000) / 1000
  return r.is_deduction ? -rounded : rounded
}

export default function MeasurementEntry() {
  const { id } = useParams()
  const editing = Boolean(id)
  const { profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()

  const { data: allSites } = useSites()
  const [woId, setWoId] = useState('')
  const [siteId, setSiteId] = useState('')
  const [date, setDate] = useState('')
  const [location, setLocation] = useState('')
  const [standard, setStandard] = useState('IS 1200')
  const [remarks, setRemarks] = useState('')
  const [rows, setRows] = useState<Row[]>([blank()])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const today = useMemo(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [])
  useEffect(() => { if (!date) setDate(today) }, [today, date])

  /**
   * The orders you may measure against.
   *
   * APPROVED is not enough. Until the contractor has ACKNOWLEDGED the order
   * there is no contract to measure against — that is what VENDACK means, and
   * it is why rpc_wo_acknowledge exists. An engineer who fills a page against
   * an unacknowledged order would be refused at certification; better he never
   * sees it in the list.
   */
  const { data: orders } = useQuery<any>('mb_open_orders',
    () => supabase.from('v_work_order_register').select('*')
      .in('status', ['VENDACK', 'PARTIALLY_FULFILLED'])
      .order('date_created', { ascending: false }),
    [], { cacheable: false })

  const { data: woLines } = useQuery<any>(
    `mb_entry_lines_${woId}`,
    () => supabase.from('v_wo_line_progress').select('*')
      .eq('wo_id', woId || '__none__').order('sl_no'),
    [woId], { cacheable: false, idField: 'wo_line_id', enabled: Boolean(woId) })

  // ---- load, when editing -------------------------------------------------
  useEffect(() => {
    if (!id) return
    void (async () => {
      const { data: m } = await supabase.from('wo_measurements').select('*')
        .eq('id', id).maybeSingle()
      if (!m) return
      setWoId(m.wo_id); setSiteId(m.site_id)
      setDate(m.measurement_date ?? ''); setLocation(m.location_ref ?? '')
      setStandard(m.measurement_standard ?? 'IS 1200'); setRemarks(m.remarks ?? '')
      const { data: ls } = await supabase.from('wo_measurement_lines').select('*')
        .eq('mb_id', id).order('sl_no')
      if (ls?.length) {
        setRows(ls.map((l: any) => ({
          key: l.id, wo_line_id: l.wo_line_id, description: l.description ?? '',
          is_deduction: Boolean(l.is_deduction), nos: String(l.nos ?? 1),
          dim_l: l.dim_l == null ? '' : String(l.dim_l),
          dim_b: l.dim_b == null ? '' : String(l.dim_b),
          dim_d: l.dim_d == null ? '' : String(l.dim_d),
          remarks: l.remarks ?? '',
        })))
      }
    })()
  }, [id])

  const order = (orders as any[]).find((o) => o.id === woId)
  useEffect(() => { if (order && !editing) setSiteId(order.site_id) }, [order, editing])

  function setRow(key: string, patch: Partial<Row>) {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  /**
   * The ceiling, per work order line, INCLUDING what this page adds.
   *
   * qty_certified from the view already excludes drafts — only a CERTIFIED page
   * counts — so adding this page's own rows to it gives exactly what the
   * certification trigger will compute.
   */
  const perLine = useMemo(() => {
    const m: Record<string, {
      wl: any; allowed: number; already: number; thisPage: number
      left: number; over: number
    }> = {}
    for (const wl of woLines as any[]) {
      const thisPage = rows.filter((r) => r.wo_line_id === wl.wo_line_id)
        .reduce((a, r) => a + lineQty(r), 0)
      const allowed = Number(wl.qty_allowed) || 0
      const already = Number(wl.qty_certified) || 0
      m[wl.wo_line_id] = {
        wl, allowed, already, thisPage,
        left: allowed - already - thisPage,
        over: Math.max(0, already + thisPage - allowed),
      }
    }
    return m
  }, [woLines, rows])

  const touched = Object.values(perLine).filter((p) => Math.abs(p.thisPage) > 0.0001)
  const overruns = touched.filter((p) => p.over > 0.001)

  const pageValue = touched.reduce(
    (a, p) => a + p.thisPage * (Number(p.wl.rate) || 0), 0)

  // ---- what would stop this being saved ----------------------------------
  const problems = useMemo(() => {
    const p: string[] = []
    if (!woId) p.push('Choose the work order this work was done under.')
    if (!date) p.push('Say which day the tape was on the work — not the day you are keying it in.')
    if (date && date > today) p.push('A measurement cannot be dated in the future.')
    if (!location.trim()) p.push('Say where. "Block A, first floor slab, grid 1–6" is what makes this page findable in two years.')
    if (rows.length === 0) p.push('A page with no dimensions on it is not a measurement.')
    rows.forEach((r, i) => {
      const at = `Entry ${i + 1}`
      if (!r.wo_line_id) p.push(`${at}: say which work order line this is measured against.`)
      if (n(r.nos) <= 0) p.push(`${at}: Nos must be more than zero.`)
      for (const [label, v] of [['Length', r.dim_l], ['Breadth', r.dim_b], ['Depth', r.dim_d]] as const) {
        if (v !== '' && n(v) <= 0)
          p.push(`${at}: ${label} is blank-or-positive. Leave it empty if it does not apply — never zero, and never a minus.`)
      }
      if (Math.abs(lineQty(r)) < 0.0005)
        p.push(`${at}: the quantity works out to nothing.`)
    })
    return p
  }, [woId, date, today, location, rows])

  async function save(thenSubmit: boolean) {
    setErr(null)
    if (problems.length) {
      setErr('Fix the points listed below first.')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setBusy(true)
    try {
      let mbId = id
      if (!mbId) {
        const { data: newId, error: idErr } = await supabase.rpc('rpc_next_mb_id',
          { p_site_id: siteId })
        if (idErr) throw new Error(idErr.message)
        mbId = newId as string
      }

      const header = {
        id: mbId, wo_id: woId, site_id: siteId,
        measurement_date: date, location_ref: location.trim(),
        measurement_standard: standard.trim() || null,
        remarks: remarks.trim() || null,
        recorded_by: profile!.id, recorder_email: profile!.email,
      }
      const { error: hErr } = id
        ? await supabase.from('wo_measurements').update(header).eq('id', mbId)
        : await supabase.from('wo_measurements').insert(header)
      if (hErr) throw new Error(hErr.message)

      // Replaced wholesale. A page that is not yet certified has nothing
      // hanging off it, and diffing would be a lot of code to save one DELETE.
      await supabase.from('wo_measurement_lines').delete().eq('mb_id', mbId)
      const { error: lErr } = await supabase.from('wo_measurement_lines').insert(
        rows.map((r, i) => ({
          mb_id: mbId, wo_line_id: r.wo_line_id, sl_no: i + 1,
          description: r.description.trim() || null,
          is_deduction: r.is_deduction,
          nos: n(r.nos),
          dim_l: r.dim_l === '' ? null : n(r.dim_l),
          dim_b: r.dim_b === '' ? null : n(r.dim_b),
          dim_d: r.dim_d === '' ? null : n(r.dim_d),
          remarks: r.remarks.trim() || null,
        })))
      if (lErr) throw new Error(lErr.message)

      if (thenSubmit) {
        const { error: sErr } = await supabase.rpc('rpc_mb_submit', { p_mb_id: mbId })
        if (sErr) throw new Error(sErr.message)
        toast.success('Submitted. It is now with the Resident Construction Manager.')
      } else {
        toast.success(`Saved as a draft — ${mbId}.`)
      }
      navigate(`/service/measurements/${mbId}`)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save the measurement')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } finally { setBusy(false) }
  }

  if (!serviceCan(profile?.role, 'recordMeasurement')) {
    return <Alert tone="warning" title="Not open to your role">
      A measurement is recorded by the Site Engineer and certified by the Resident
      Construction Manager.
    </Alert>
  }
  if (!allSites.length) return <Loading label="Loading…" />

  return (
    <div className="space-y-4">
      <button onClick={() => navigate('/service/measurements')}
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> The Measurement Book
      </button>

      <h1 className="text-xl font-bold text-slate-900">
        {editing ? `Edit ${id}` : 'Record a measurement'}
      </h1>

      {err && <Alert tone="error" title="Not saved">{err}</Alert>}

      {/* ---------- which work, where, when ---------- */}
      <Card title="The page">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Work order" required className="sm:col-span-2"
                 hint="Only orders the contractor has acknowledged. Until he has, there is no contract to measure against.">
            <Select value={woId} placeholder={(orders as any[]).length
                      ? 'Choose…' : 'No acknowledged work order at your sites yet'}
                    disabled={editing}
                    onChange={(e) => { setWoId(e.target.value); setRows([blank()]) }}
                    options={(orders as any[]).map((o) => ({
                      value: o.id, label: `${o.id} · ${o.site_name} · ${o.vendor_name}` }))} />
          </Field>
          <Field label="Measured on" required hint="The day the tape was on the work">
            <Input type="date" max={today} value={date}
                   invalid={Boolean(date && date > today)}
                   onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label="Standard applied" hint="Printed on the certified page">
            <Input value={standard} onChange={(e) => setStandard(e.target.value)}
                   placeholder="IS 1200" />
          </Field>
          <Field label="Location" required className="sm:col-span-2 lg:col-span-4"
                 hint="Which stretch of work this page covers, in your own words. This is what makes it findable in two years.">
            <Input value={location} onChange={(e) => setLocation(e.target.value)}
                   placeholder="Block A, first floor slab, grid 1–6" />
          </Field>
          <Field label="Remarks" className="sm:col-span-2 lg:col-span-4">
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Measured jointly with the contractor's supervisor. Weather dry." />
          </Field>
        </div>

        {order && (
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <Info className="mr-1 inline h-3.5 w-3.5" />
            {order.wo_title ?? order.title} — {order.vendor_name}, {rupees(order.contract_value)}.
            Acknowledged, so work may be measured against it.
          </p>
        )}
      </Card>

      {/* ---------- what is left to measure ---------- */}
      {woId && (woLines as any[]).length > 0 && (
        <Card title="Against this order"
              subtitle="Ordered plus approved variations, less what has already been certified. What this page adds is counted as you type.">
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th><th>Line</th><th>UOM</th>
                  <th className="num">Ordered &amp; varied</th>
                  <th className="num">Certified</th>
                  <th className="num">This page</th>
                  <th className="num">Left after this</th>
                </tr>
              </thead>
              <tbody>
                {(woLines as any[]).map((wl) => {
                  const p = perLine[wl.wo_line_id]
                  if (!p) return null
                  const over = p.over > 0.001
                  return (
                    <tr key={wl.wo_line_id}
                        className={over ? 'bg-red-50'
                          : Math.abs(p.thisPage) > 0.0001 ? 'bg-amber-50/50' : undefined}>
                      <td>{wl.sl_no}</td>
                      <td className="max-w-[240px]">{wl.description}</td>
                      <td>{wl.uom}</td>
                      <td className="num">{qty(p.allowed)}</td>
                      <td className="num">{qty(p.already)}</td>
                      <td className="num font-semibold">
                        {p.thisPage === 0 ? '—' : qty(p.thisPage)}
                      </td>
                      <td className={over
                        ? 'num font-bold text-red-700'
                        : 'num font-semibold text-emerald-700'}>
                        {over ? `over by ${qty(p.over)}` : qty(p.left)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ---------- THE OVER-RUN, LIVE ---------- */}
      {overruns.length > 0 && (
        <Card title={
          <span className="flex items-center gap-1.5 text-red-800">
            <TriangleAlert className="h-4 w-4" />
            {overruns.length} line{overruns.length === 1 ? '' : 's'} go past what was ordered
          </span>}
          subtitle="You can still save this page. It will not certify until somebody signs for the extra.">
          <ul className="space-y-1.5">
            {overruns.map((p) => (
              <li key={p.wl.wo_line_id} className="flex items-start gap-2 text-sm text-slate-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                <span>
                  <strong>Line {p.wl.sl_no}</strong> — {p.wl.description}: ordered and varied{' '}
                  {qty(p.allowed)} {p.wl.uom}, already certified {qty(p.already)}, this page{' '}
                  {qty(p.thisPage)}. <strong className="text-red-700">
                  Over by {qty(p.over)} {p.wl.uom}.</strong>
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-3 rounded-lg border border-amber-300 border-l-4 border-l-amber-600 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
            <p className="font-semibold">This is not a refusal to measure what you measured.</p>
            <p className="mt-0.5">
              Record what is actually on the ground — that is the whole job. But the page will
              not certify while it sits above the ordered quantity, so tell Procurement: they
              raise a <strong>Variation Order</strong> on {woId} for the extra, and once it is
              approved this page certifies unchanged, with nothing retyped.
            </p>
          </div>
        </Card>
      )}

      {/* ---------- the six columns ---------- */}
      <Card title="Entries"
            subtitle="Description · Nos · Length · Breadth · Depth · Quantity. Leave a dimension EMPTY where it does not apply — a running metre has no breadth, and zero would give nothing at all."
            actions={
              <button className="btn-secondary btn-sm" disabled={!woId}
                      onClick={() => setRows((rs) => [...rs, blank()])}>
                <Plus className="h-4 w-4" /> Add an entry
              </button>
            }>
        {!woId ? (
          <p className="py-6 text-center text-sm text-slate-500">
            Choose a work order first — the entries are measured against its lines.
          </p>
        ) : (
          <div className="space-y-3">
            {rows.map((r, i) => {
              const wl = (woLines as any[]).find((w) => w.wo_line_id === r.wo_line_id)
              const q = lineQty(r)
              return (
                <div key={r.key}
                     className={r.is_deduction
                       ? 'rounded-lg border border-red-200 bg-red-50/40 p-3'
                       : 'rounded-lg border border-slate-200 p-3'}>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                      Entry {i + 1}
                    </p>
                    <div className="flex items-center gap-3">
                      <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-700">
                        <input type="checkbox" className="h-4 w-4 rounded border-slate-300"
                               checked={r.is_deduction}
                               onChange={(e) => setRow(r.key, { is_deduction: e.target.checked })} />
                        <Minus className="h-3.5 w-3.5 text-red-500" />
                        This entry is a deduction
                      </label>
                      <button className="btn-ghost btn-sm text-red-600"
                              onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
                              disabled={rows.length === 1}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Field label="Measured against" required className="sm:col-span-2"
                           hint={wl?.description ? `Measured as: ${
                             (woLines as any[]).find((w) => w.wo_line_id === r.wo_line_id)?.uom ?? ''
                           }` : undefined}>
                      <Select value={r.wo_line_id} placeholder="Which line of the order…"
                              onChange={(e) => setRow(r.key, { wo_line_id: e.target.value })}
                              options={(woLines as any[]).map((w) => ({
                                value: w.wo_line_id,
                                label: `${w.sl_no}. ${w.description} · ${w.uom}` }))} />
                    </Field>
                    <Field label="Description" className="sm:col-span-2"
                           hint="What this entry covers, if not simply the order line">
                      <Input value={r.description}
                             onChange={(e) => setRow(r.key, { description: e.target.value })}
                             placeholder={r.is_deduction
                               ? 'Door opening D1'
                               : wl?.description ?? 'Slab, grid 1–3'} />
                    </Field>

                    <Field label="Nos" required hint="How many of this shape">
                      <Input type="number" step="0.001" min="0.001" value={r.nos}
                             invalid={n(r.nos) <= 0}
                             onChange={(e) => setRow(r.key, { nos: e.target.value })} />
                    </Field>
                    <Field label="Length" hint="Blank if it does not apply">
                      <Input type="number" step="0.001" min="0.001" value={r.dim_l}
                             onChange={(e) => setRow(r.key, { dim_l: e.target.value })} />
                    </Field>
                    <Field label="Breadth" hint="Blank if it does not apply">
                      <Input type="number" step="0.001" min="0.001" value={r.dim_b}
                             onChange={(e) => setRow(r.key, { dim_b: e.target.value })} />
                    </Field>
                    <Field label="Depth" hint="Blank if it does not apply">
                      <Input type="number" step="0.001" min="0.001" value={r.dim_d}
                             onChange={(e) => setRow(r.key, { dim_d: e.target.value })} />
                    </Field>

                    <Field label="Remarks" className="sm:col-span-3">
                      <Input value={r.remarks}
                             onChange={(e) => setRow(r.key, { remarks: e.target.value })}
                             placeholder="Joint measurement, contractor's supervisor present" />
                    </Field>
                    <div className="flex items-end pb-2">
                      <p className="text-sm">
                        <span className="text-slate-500">Quantity </span>
                        <span className={q < 0
                          ? 'font-bold tabular-nums text-red-700'
                          : 'font-bold tabular-nums text-slate-800'}>
                          {qty(q)} {wl?.uom ?? ''}
                        </span>
                      </p>
                    </div>
                  </div>

                  {r.is_deduction && (
                    <p className="mt-2 rounded bg-white/70 px-2 py-1 text-[11px] text-red-800">
                      Enter the opening&rsquo;s <strong>positive</strong> dimensions. The
                      quantity subtracts itself — never type a minus sign.
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {touched.length > 0 && (
          <div className="mt-4 rounded-lg bg-slate-50 px-4 py-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm text-slate-600">
                {rows.length} entr{rows.length === 1 ? 'y' : 'ies'} against{' '}
                {touched.length} order line{touched.length === 1 ? '' : 's'}
              </p>
              <p className="text-lg font-bold tabular-nums text-slate-900">
                {rupees(pageValue)}
              </p>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              What this page is worth at contract rates. It becomes billable only when the
              Resident Construction Manager certifies it.
            </p>
          </div>
        )}
      </Card>

      {problems.length > 0 && (
        <Card title={`${problems.length} thing${problems.length === 1 ? '' : 's'} to fix`}>
          <ul className="space-y-1.5">
            {problems.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-secondary" disabled={busy} onClick={() => void save(false)}>
          Save as draft
        </button>
        <button className="btn-primary" disabled={busy || problems.length > 0}
                onClick={() => void save(true)}>
          {busy ? 'Working…' : 'Save and submit for certification'}
        </button>
        {overruns.length > 0 && (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700">
            <Ruler className="h-3.5 w-3.5" />
            It will submit, but it will not certify until the over-run is covered by a
            Variation Order.
          </span>
        )}
      </div>
    </div>
  )
}
